import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWeight,
  faUser,
  faTable,
  faChartLine,
  faPlus,
  faTrash,
  faHeartbeat,
  faArrowTrendUp,
  faArrowTrendDown,
  faEquals,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import apiService from "../../ApiService";

const validNames = ["Kavi", "Renu", "Veda", "Senthil"];

const PersonWeightsForm = () => {
  const [personWeights, setPersonWeights] = useState([]);
  const [formData, setFormData] = useState({
    person_name: validNames[0],
    weight: "",
    measurement_unit: "kg",
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("chart"); // "chart", "table", "statistics"
  const [selectedPerson, setSelectedPerson] = useState("all");
  const [showAddForm, setShowAddForm] = useState(false);

  // Color palette for different people
  const personColors = {
    Kavi: "#3B82F6",
    Renu: "#EF4444",
    Veda: "#10B981",
    Senthil: "#F59E0B",
  };

  // Fetch all records on mount
  useEffect(() => {
    fetchPersonWeights();
  }, []);

  const fetchPersonWeights = async () => {
    setLoading(true);
    setError("");
    const data = await apiService.getPersonWeights();
    if (data) setPersonWeights(data);
    else setError("Failed to fetch person weights.");
    setLoading(false);
  };

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit form - convert lb to kg if needed before sending
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    let { person_name, weight, measurement_unit, year, month } = formData;

    if (!person_name || !weight) {
      setError("Name and Weight are required");
      return;
    }

    let weightInKg = parseFloat(weight);
    if (measurement_unit === "lb") {
      weightInKg = weightInKg * 0.453592; // lb → kg conversion
    }

    const data = await apiService.createPersonWeight({
      person_name,
      weight: weightInKg,
      measurement_unit: "kg", // always store kg
      year: parseInt(year, 10),
      month: parseInt(month, 10),
    });

    if (data && data.message !== "Unable to create record.") {
      fetchPersonWeights();
      setFormData({
        person_name: validNames[0],
        weight: "",
        measurement_unit: "kg",
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      });
    } else {
      setError("Failed to create record");
    }
  };

  // Delete record by id
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    const data = await apiService.deletePersonWeight(id);
    if (data && data.message === "Record deleted.") {
      fetchPersonWeights();
    } else {
      setError("Failed to delete record");
    }
  };

  // Group data by year-month, pivot persons as columns
  const summaryData = personWeights.reduce((acc, record) => {
    const key = `${record.year}-${record.month}`;
    if (!acc[key]) {
      acc[key] = { year: record.year, month: record.month };
    }
    acc[key][record.person_name] = parseFloat(record.weight).toFixed(2) + " kg";
    return acc;
  }, {});

  // Prepare chart data
  const prepareChartData = () => {
    const chartData = {};

    personWeights.forEach((record) => {
      const key = `${record.year}-${String(record.month).padStart(2, "0")}`;
      if (!chartData[key]) {
        chartData[key] = {
          period: `${record.year}-${String(record.month).padStart(2, "0")}`,
          month: new Date(record.year, record.month - 1).toLocaleString(
            "default",
            { month: "short" },
          ),
          year: record.year,
        };
      }
      chartData[key][record.person_name] = record.weight;
    });

    return Object.values(chartData).sort((a, b) =>
      a.period.localeCompare(b.period),
    );
  };

  // Get statistics for each person
  const getPersonStatistics = () => {
    const stats = {};

    validNames.forEach((name) => {
      const personData = personWeights
        .filter((record) => record.person_name === name)
        .sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return a.month - b.month;
        });

      if (personData.length > 0) {
        const weights = personData.map((r) => parseFloat(r.weight));
        const latest = personData[personData.length - 1];

        // Find last month's data for comparison
        const currentDate = new Date(latest.year, latest.month - 1);
        const lastMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - 1,
        );
        const lastMonthYear = lastMonth.getFullYear();
        const lastMonthMonth = lastMonth.getMonth() + 1;

        const lastMonthData = personData.find(
          (r) => r.year === lastMonthYear && r.month === lastMonthMonth,
        );

        const currentWeight = parseFloat(latest.weight);
        const previousWeight = lastMonthData
          ? parseFloat(lastMonthData.weight)
          : null;
        const weightChange = previousWeight
          ? currentWeight - previousWeight
          : 0;

        stats[name] = {
          current: currentWeight,
          previous: previousWeight,
          change: weightChange,
          min: Math.min(...weights),
          max: Math.max(...weights),
          avg: weights.reduce((a, b) => a + b, 0) / weights.length,
          records: personData.length,
          trend: previousWeight
            ? currentWeight > previousWeight
              ? "up"
              : currentWeight < previousWeight
                ? "down"
                : "same"
            : "none",
        };
      }
    });

    return stats;
  };

  const chartData = prepareChartData();
  const personStats = getPersonStatistics();

  // Sorted keys for display order
  const sortedKeys = Object.keys(summaryData).sort((a, b) => {
    const [yearA, monthA] = a.split("-").map(Number);
    const [yearB, monthB] = b.split("-").map(Number);

    if (yearA !== yearB) return yearB - yearA; // newer year first
    return monthB - monthA; // newer month first if year is same
  });

  // Get unique person names for table headers
  const allPersons = validNames; // use validNames so header order is consistent

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-6 mb-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 p-3 rounded-xl">
              <FontAwesomeIcon
                icon={faHeartbeat}
                className="text-white text-2xl"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Health Dashboard
              </h2>
              <p className="text-gray-600">
                Track weight progress and health metrics for family members
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => fetchPersonWeights()}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faRefresh} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Add Record</span>
            </button>
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="flex space-x-2">
          {[
            { key: "chart", label: "📊 Chart View", icon: faChartLine },
            { key: "statistics", label: "📈 Statistics", icon: faArrowTrendUp },
            { key: "table", label: "📋 Table View", icon: faUser },
          ].map((mode) => (
            <button
              key={mode.key}
              onClick={() => setViewMode(mode.key)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                viewMode === mode.key
                  ? "bg-blue-600 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <FontAwesomeIcon icon={mode.icon} />
              <span>{mode.label}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faWeight} className="text-red-600" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Add Record Form */}
      {showAddForm && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <FontAwesomeIcon icon={faPlus} className="text-green-600" />
            <span>Add New Weight Record</span>
          </h3>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-6 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FontAwesomeIcon icon={faUser} className="mr-1" />
                Person
              </label>
              <select
                name="person_name"
                value={formData.person_name}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {validNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FontAwesomeIcon icon={faWeight} className="mr-1" />
                Weight
              </label>
              <input
                type="number"
                name="weight"
                placeholder="Enter weight"
                step="0.01"
                value={formData.weight}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit
              </label>
              <select
                name="measurement_unit"
                value={formData.measurement_unit}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="kg">Kilograms</option>
                <option value="lb">Pounds</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Month
              </label>
              <select
                name="month"
                value={formData.month}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>Jan</option>
                <option value={2}>Feb</option>
                <option value={3}>Mar</option>
                <option value={4}>Apr</option>
                <option value={5}>May</option>
                <option value={6}>Jun</option>
                <option value={7}>Jul</option>
                <option value={8}>Aug</option>
                <option value={9}>Sep</option>
                <option value={10}>Oct</option>
                <option value={11}>Nov</option>
                <option value={12}>Dec</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <input
                type="number"
                name="year"
                min="2020"
                max="2030"
                value={formData.year}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>{loading ? "Adding..." : "Add Record"}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Statistics View */}
      {viewMode === "statistics" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {Object.entries(personStats).map(([name, stats]) => (
            <div
              key={name}
              className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: personColors[name] }}
                  ></div>
                  <h3 className="text-lg font-bold text-gray-900">{name}</h3>
                </div>
                <div className="flex items-center space-x-1">
                  <FontAwesomeIcon
                    icon={
                      stats.trend === "up"
                        ? faArrowTrendUp
                        : stats.trend === "down"
                          ? faArrowTrendDown
                          : faEquals
                    }
                    className={`text-sm ${
                      stats.trend === "up"
                        ? "text-red-500"
                        : stats.trend === "down"
                          ? "text-green-500"
                          : "text-gray-400"
                    }`}
                  />
                  <span
                    className={`text-sm font-medium ${
                      stats.trend === "up"
                        ? "text-red-600"
                        : stats.trend === "down"
                          ? "text-green-600"
                          : "text-gray-600"
                    }`}
                  >
                    {stats.change > 0 ? "+" : ""}
                    {stats.change.toFixed(1)} kg
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Current:</span>
                  <span
                    className="font-bold text-2xl"
                    style={{ color: personColors[name] }}
                  >
                    {stats.current} kg
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Average:</span>
                  <span className="font-medium">{stats.avg.toFixed(1)} kg</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Range:</span>
                  <span className="font-medium">
                    {stats.min} - {stats.max} kg
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Records:</span>
                  <span className="font-medium">{stats.records}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chart View */}
      {viewMode === "chart" && chartData.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
              <FontAwesomeIcon icon={faChartLine} className="text-blue-600" />
              <span>Weight Trends</span>
            </h3>

            <div className="flex items-center space-x-3">
              <label className="text-sm font-medium text-gray-700">
                Filter:
              </label>
              <select
                value={selectedPerson}
                onChange={(e) => setSelectedPerson(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Members</option>
                {validNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis domain={["dataMin - 5", "dataMax + 5"]} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                  }}
                />
                <Legend />
                {validNames.map((name) => {
                  if (selectedPerson !== "all" && selectedPerson !== name)
                    return null;
                  return (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={personColors[name]}
                      strokeWidth={3}
                      dot={{ fill: personColors[name], strokeWidth: 2, r: 6 }}
                      connectNulls={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
              <FontAwesomeIcon icon={faTable} className="text-blue-600" />
              <span>Weight Records Table</span>
            </h3>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading records...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Month
                    </th>
                    {allPersons.map((person) => (
                      <th
                        key={person}
                        className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                        style={{ color: personColors[person] }}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: personColors[person] }}
                          ></div>
                          <span>{person}</span>
                        </div>
                      </th>
                    ))}
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedKeys.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3 + allPersons.length}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center space-y-3">
                          <FontAwesomeIcon
                            icon={faWeight}
                            className="text-4xl text-gray-300"
                          />
                          <p className="text-lg font-medium">
                            No weight records found
                          </p>
                          <p className="text-sm">
                            Add your first weight record to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedKeys.map((key) => (
                      <tr
                        key={key}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {summaryData[key].year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(
                            summaryData[key].year,
                            summaryData[key].month - 1,
                          ).toLocaleString("default", { month: "long" })}
                        </td>
                        {allPersons.map((person) => (
                          <td
                            key={person}
                            className="px-6 py-4 whitespace-nowrap text-center text-sm"
                          >
                            {summaryData[key][person] ? (
                              <span
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                                style={{
                                  backgroundColor: `${personColors[person]}20`,
                                  color: personColors[person],
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faWeight}
                                  className="mr-1 text-xs"
                                />
                                {summaryData[key][person]}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        ))}
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                          <div className="flex justify-center space-x-2">
                            {personWeights
                              .filter(
                                (p) =>
                                  p.year === summaryData[key].year &&
                                  p.month === summaryData[key].month,
                              )
                              .map((record) => (
                                <button
                                  key={record.id}
                                  onClick={() => handleDelete(record.id)}
                                  className="inline-flex items-center px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                                  title={`Delete ${record.person_name} record`}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="text-xs mr-1"
                                  />
                                  <span className="text-xs">
                                    {record.person_name}
                                  </span>
                                </button>
                              ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonWeightsForm;
